import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTeamsDDL,
  selectTeamsState,
} from '@/redux-toolkit/slice/teamsSlice';
import type { CompanyTeam } from '@/apis/settings';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import type { AppDispatch } from '@/redux-toolkit/store';
import TeamsDropdownLB from '../shared/TeamsDropdownLB';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import { FieldErrorLabel, FieldLabel } from '../shared';
import { SlackActionFields } from './SlackActionNodeInfo';
import { useTeamGroups } from '@/hooks/useTeamGroups';

/**
 * Customization UI for SLACK_ACTION nodes.
 */
export const SlackActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const recipientTeamsError = validationErrors?.find(
    (e) => e.field === SlackActionFields.RECIPIENT_TEAMS
  );
  const messageTemplateError = validationErrors?.find(
    (e) => e.field === SlackActionFields.MESSAGE_TEMPLATE
  );
  const dispatch = useDispatch<AppDispatch>();
  const { getToken } = useToken();
  const teamsState = useSelector(selectTeamsState);
  // Team groups expand to their teams in the recipients picker.
  const teamGroups = useTeamGroups(teamsState.list.map((team) => team.id));

  const [selectedTeams, setSelectedTeams] = useState<CompanyTeam[]>([]);
  const messageChunks = nodeData.slack_action?.message_chunks ?? [];

  // Fetch teams on mount if not already loaded
  useEffect(() => {
    const token = getToken();
    if (token && teamsState.list.length === 0) {
      // Fire and forget: the thunk settles with a rejected action, never throws.
      const _ = dispatch(fetchTeamsDDL(token));
    }
  }, [dispatch, getToken, teamsState.list.length]);

  // Update local state when nodeData changes (e.g., node switching)
  useEffect(() => {
    const teamIds = nodeData.slack_action?.recipient_team_ids || [];
    const teams = teamIds
      .map((id) => teamsState.list.find((team) => team.id === id))
      .filter((team): team is CompanyTeam => team !== undefined);
    setSelectedTeams(teams);
  }, [nodeData, teamsState.list]);

  const handleTeamsChange = (teams: CompanyTeam[]) => {
    setSelectedTeams(teams);
    onChange({
      slack_action: {
        recipient_team_ids: teams.map((team) => team.id),
        message_chunks: messageChunks,
      },
    });
  };

  const handleMessageChange = (newChunks: TextTemplateChunk[]) => {
    onChange({
      slack_action: {
        recipient_team_ids: selectedTeams.map((team) => team.id),
        message_chunks: newChunks,
      },
    });
  };

  const [teamsSearch, setTeamsSearch] = useState<string>('');
  const teamsLoading = teamsState.loading;

  const filteredTeamsList = useMemo(() => {
    if (!teamsSearch) return teamsState.list;
    const query = teamsSearch.toLowerCase();
    return teamsState.list.filter((team) =>
      team.name?.toLowerCase().includes(query)
    );
  }, [teamsState.list, teamsSearch]);

  return (
    <>
      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Recipients
        </FieldLabel>
        {teamsLoading ? (
          <div>Loading teams...</div>
        ) : teamsState.list.length === 0 ? (
          <div>No teams available</div>
        ) : (
          <TeamsDropdownLB
            teamsOptions={filteredTeamsList}
            selectedTeams={selectedTeams}
            onChange={handleTeamsChange}
            disabled={isReadOnly}
            provider='slack'
            teamsSearch={teamsSearch}
            onSearch={setTeamsSearch}
            teamGroups={teamGroups}
            groupTeamsOptions={teamsState.list}
          />
        )}
        <FieldErrorLabel error={recipientTeamsError} />
      </div>

      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Message Template
        </FieldLabel>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={messageChunks}
          onChange={handleMessageChange}
          placeholder='Enter your Slack message template...'
          disabled={isReadOnly}
        />
        <FieldErrorLabel error={messageTemplateError} />
      </div>
    </>
  );
};
